import { Box, createStyles, makeStyles, Theme, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import React, { FunctionComponent, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Discount as DiscountModel } from 'model/NewMultipleBooking';
import { PercentageField2, PriceField2 } from 'components/UI';
import useNonInitialEffect from '@versiondos/hooks';

interface DiscountProps {
    discount: DiscountModel;
    max?: number;
    default?: string;
    onChangeDiscount: (discount: DiscountModel) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            width: '100%'
        },
        discountContainer: {
            display: 'flex',
            flexDirection: 'column',
            marginRight: 20
        },
        discount: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%'
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 20
        },
        text: {
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '16px',
            alignSelf: 'center'
        },
        button: {
            display: 'flex',
            justifyContent: 'center',
            width: 62,
            height: 54,
            borderRadius: 6,
            backgroundColor: '#F1F1F1',
            marginRight: 8,
            cursor: 'pointer'
        },
        buttonSelected: {
            backgroundColor: '#000'
        },
        selected: {
            color: '#fff'
        },
        amountContainer: {
            width: 113,
            marginTop: 39
        },
        amountContainerMobile: {
            width: '100%'
        },
        field: {
            '& input': {
                paddingLeft: '0px',
                paddingRight: '0px',
                borderColor: '#d0d0d0 !important',
                height: '24px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #d0d0d0 !important'
            },
            '& .MuiOutlinedInput-adornedStart': {
                paddingLeft: '0px'
            }
        },
        centeredPercentage: {
            '& input': {
                paddingLeft: '41px'
            }
        },
        centeredAmount: {
            '& input': {
                paddingLeft: '10px'
            }
        }
    })
);

export const Discount: FunctionComponent<DiscountProps> = props => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [value, setValue] = useState<DiscountModel>(props.discount);
    const [defaultState, setDefault] = useState<string | undefined>(props.default);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            setDefault(undefined);
            props.onChangeDiscount({
                ...props.discount,
                value: value?.value || 0.0
            });
        }, 500);
        return () => clearTimeout(timer);
    }, [value]);

    useEffect(() => {
        if (props.default) {
            setDefault(props.default);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.default]);

    const handlerChangeAmount = (value?: number) => {
        setValue(prev => ({
            ...prev,
            type: 'amount',
            value: value || 0
        }));
    };

    const handleKeyPress = (event: any) => {
        const value = event?.target?.value + event.key;

        if (props.discount?.type === 'amount') {
            const valueFormatted = value?.replace('.', '');

            if (valueFormatted?.length > 2) {
                const firstPart = valueFormatted.slice(0, valueFormatted.length - 2);
                const lastPart = valueFormatted.slice(valueFormatted.length - 2);
                const result = firstPart + '.' + lastPart;

                if (!!value && (!!props.max || props.max === 0) && Number(result) > props.max) {
                    event.preventDefault();
                }
            }
        } else {
            if (!!value && (!!props.max || props.max === 0) && Number(value) > props.max) {
                event.preventDefault();
            }
        }
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.discountContainer}>
                <Typography className={classes.title}>Discount</Typography>
                <Box className={classes.discount}>
                    <Box
                        className={clsx(classes.button, {
                            [classes.buttonSelected]: props.discount?.type === 'amount'
                        })}
                        onClick={() => {
                            props.onChangeDiscount({
                                ...props.discount,
                                type: 'amount',
                                value: 0
                            });
                            setDefault('0.00');
                        }}
                    >
                        <Typography
                            className={clsx(classes.text, { [classes.selected]: props.discount?.type === 'amount' })}
                        >
                            $
                        </Typography>
                    </Box>
                    <Box
                        className={clsx(classes.button, {
                            [classes.buttonSelected]: props.discount?.type === 'percentage'
                        })}
                        onClick={() => {
                            props.onChangeDiscount({
                                ...props.discount,
                                type: 'percentage',
                                value: 0
                            });
                            setDefault('0');
                        }}
                    >
                        <Typography
                            className={clsx(classes.text, {
                                [classes.selected]: props.discount?.type === 'percentage'
                            })}
                        >
                            %
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box className={clsx(classes.amountContainer, { [classes.amountContainerMobile]: isMobile })}>
                {props.discount?.type === 'percentage' ? (
                    <PercentageField2
                        max={props.max}
                        placeholder="0.00"
                        default={defaultState}
                        value={value?.value}
                        onChange={handlerChangeAmount}
                        alignRight={isMobile ? true : false}
                        className={clsx(classes.field, { [classes.centeredPercentage]: !isMobile })}
                        scale={0}
                        onKeyPress={handleKeyPress}
                    />
                ) : (
                    <PriceField2
                        scale={2}
                        default={defaultState}
                        precision={6}
                        placeholder="0.00"
                        value={value?.value}
                        onChange={handlerChangeAmount}
                        className={clsx(classes.field, { [classes.centeredAmount]: !isMobile })}
                        onKeyPress={handleKeyPress}
                    />
                )}
            </Box>
        </Box>
    );
};

export default Discount;
